import React, { useEffect, useState } from "react";
import { Shell } from "../components/Shell";
import { Tabs } from "../components/Tabs";
import { api, ApiError, descargarArchivo } from "../api";
import { useAuth } from "../auth/AuthContext";

type Empleado = {
  id: number; nombre: string; tipoDocumento: string; numeroDocumento: string; fechaIngreso: string; fechaRetiro: string | null;
  cargo: string | null; centroCostoId: number | null; eps: string | null; fondoPension: string | null; arlNivelRiesgo: string;
  cajaCompensacion: string | null; banco: string | null; numeroCuenta: string | null; tipoCuenta: string; estado: string;
};
type Contrato = { id: number; empleadoId: number; tipoContrato: string; tipoSalario: string; salarioBase: number; fechaInicio: string; fechaFin: string | null; estado: string };
type Periodo = { id: number; anio: number; mes: number; quincena: number | null; fechaInicio: string; fechaFin: string; estado: string };
type Novedad = { id: number; empleadoId: number; periodoId: number; tipo: string; cantidad: number | null; valor: number; observaciones: string | null };
type Liquidacion = { id: number; periodoId: number; empleadoId: number; empleadoNombre?: string; diasTrabajados: number; salarioBase: number; totalDevengado: number; totalDeducciones: number; netoPagado: number; estado: string; comprobanteId: number | null };

const money = (n: number) => `$${Math.round(n).toLocaleString()}`;
const MESES = ["", "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const TIPO_CONTRATO_LABEL: Record<string, string> = { termino_indefinido: "Término indefinido", termino_fijo: "Término fijo", obra_labor: "Obra o labor", aprendizaje: "Aprendizaje" };
const TIPO_NOVEDAD_LABEL: Record<string, string> = {
  horas_extras_diurnas: "Horas extra diurnas", horas_extras_nocturnas: "Horas extra nocturnas",
  horas_extras_dominicales_festivas: "Horas extra dominicales/festivas", incapacidad: "Incapacidad",
  licencia_remunerada: "Licencia remunerada", licencia_no_remunerada: "Licencia no remunerada",
  vacaciones_disfrutadas: "Vacaciones disfrutadas", bonificacion: "Bonificación", comision: "Comisión",
  descuento: "Descuento", embargo: "Embargo", prestamo: "Préstamo", otro_devengado: "Otro devengado", otro_deduccion: "Otra deducción",
};
// El valor de una hora extra lo fija la ley (recargo % sobre el valor hora del
// contrato del empleado, ver nomina_utils.calcular_valor_hora_extra en el
// backend) — quien registra la novedad solo indica cuántas horas, nunca el
// valor a pagar.
const TIPOS_HORA_EXTRA = new Set(["horas_extras_diurnas", "horas_extras_nocturnas", "horas_extras_dominicales_festivas"]);
const CONCEPTO_LABEL: Record<string, string> = {
  salario_basico: "Salario básico", auxilio_transporte: "Auxilio de transporte", salud_empleado: "Salud (empleado)",
  pension_empleado: "Pensión (empleado)", fondo_solidaridad_pensional: "Fondo de solidaridad pensional",
  ...TIPO_NOVEDAD_LABEL,
};

export function Nomina() {
  const { tienePermiso } = useAuth();
  const puedeCrear = tienePermiso("nomina:crear");
  const puedeEditar = tienePermiso("nomina:editar");
  const puedeLiquidar = tienePermiso("nomina:liquidar");
  const puedePagar = tienePermiso("nomina:pagar");

  const [tab, setTab] = useState("empleados");
  const [empleados, setEmpleados] = useState<Empleado[]>([]);
  const [periodos, setPeriodos] = useState<Periodo[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cargarBase = async () => {
    setCargando(true);
    setError(null);
    try {
      const [emp, per] = await Promise.all([api.get("/nomina/empleados"), api.get("/nomina/periodos")]);
      setEmpleados(emp); setPeriodos(per);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo cargar la información de nómina.");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargarBase(); }, []);

  return (
    <Shell titulo="Nómina" breadcrumb="Nómina y Cumplimiento Laboral">
      <p className="text-sm text-textmuted mb-4">
        Empleados, contratos, novedades y liquidación de nómina con generación automática del comprobante contable,
        aportes a seguridad social y parafiscales, prestaciones sociales provisionadas y nómina electrónica DIAN (simulada).
      </p>
      <Tabs
        tabs={[
          { key: "empleados", label: "Empleados" },
          { key: "contratos", label: "Contratos" },
          { key: "periodos", label: "Períodos" },
          { key: "novedades", label: "Novedades" },
          { key: "liquidaciones", label: "Liquidación" },
          { key: "seguridad-social", label: "Seguridad social" },
          { key: "prestaciones", label: "Prestaciones sociales" },
          { key: "nomina-electronica", label: "Nómina electrónica" },
          { key: "parametros", label: "Parámetros" },
        ]}
        activo={tab} onChange={setTab}
      />
      {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-4">{error}</div>}
      {cargando ? (
        <p className="text-sm text-textmuted">Cargando...</p>
      ) : (
        <>
          {tab === "empleados" && <TabEmpleados empleados={empleados} puedeCrear={puedeCrear} puedeEditar={puedeEditar} recargar={cargarBase} />}
          {tab === "contratos" && <TabContratos empleados={empleados} puedeCrear={puedeCrear} />}
          {tab === "periodos" && <TabPeriodos periodos={periodos} puedeCrear={puedeCrear} puedeLiquidar={puedeLiquidar} recargar={cargarBase} />}
          {tab === "novedades" && <TabNovedades empleados={empleados} periodos={periodos} puedeCrear={puedeCrear} puedeEditar={puedeEditar} />}
          {tab === "liquidaciones" && <TabLiquidaciones empleados={empleados} periodos={periodos} puedeLiquidar={puedeLiquidar} puedePagar={puedePagar} />}
          {tab === "seguridad-social" && <TabSeguridadSocial periodos={periodos} />}
          {tab === "prestaciones" && <TabPrestaciones empleados={empleados} />}
          {tab === "nomina-electronica" && <TabNominaElectronica />}
          {tab === "parametros" && <TabParametros puedeEditar={puedeEditar} />}
        </>
      )}
    </Shell>
  );
}

// ---------------------------------------------------------------- empleados
function TabEmpleados({ empleados, puedeCrear, puedeEditar, recargar }: {
  empleados: Empleado[]; puedeCrear: boolean; puedeEditar: boolean; recargar: () => void;
}) {
  const [modalAbierto, setModalAbierto] = useState(false);
  const [retirarId, setRetirarId] = useState<number | null>(null);
  const [descargando, setDescargando] = useState<string | null>(null);
  const [errorDescarga, setErrorDescarga] = useState<string | null>(null);

  const retirar = async (fechaRetiro: string) => {
    if (!retirarId) return;
    try { await api.post(`/nomina/empleados/${retirarId}/retirar`, { fechaRetiro }); setRetirarId(null); recargar(); } catch { /* noop */ }
  };

  // Certificado laboral "a quien interese" (Art. 57 num. 7 CST) — se puede
  // descargar en PDF o Word para cualquier empleado, activo o retirado.
  const descargarCertificado = async (emp: Empleado, formato: "pdf" | "docx") => {
    const clave = `${emp.id}-${formato}`;
    setDescargando(clave);
    setErrorDescarga(null);
    try {
      await descargarArchivo(`/nomina/empleados/${emp.id}/certificado-laboral?formato=${formato}`, `certificado_laboral_${emp.numeroDocumento}.${formato}`);
    } catch (err) {
      setErrorDescarga(err instanceof ApiError ? err.message : `No se pudo generar el certificado laboral de ${emp.nombre}.`);
    } finally {
      setDescargando(null);
    }
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        {puedeCrear && <button className="btn-primary" onClick={() => setModalAbierto(true)}>+ Nuevo empleado</button>}
      </div>
      {errorDescarga && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-3">{errorDescarga}</div>}
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-borderc">
            <th className="table-th">Nombre</th><th className="table-th">Documento</th><th className="table-th">Cargo</th>
            <th className="table-th">EPS</th><th className="table-th">ARL</th><th className="table-th">Ingreso</th>
            <th className="table-th">Estado</th><th className="table-th"></th>
          </tr></thead>
          <tbody>
            {empleados.map((e) => (
              <tr key={e.id} className="border-b border-borderc last:border-0">
                <td className="table-td font-medium">{e.nombre}</td>
                <td className="table-td text-xs text-textmuted">{e.tipoDocumento} {e.numeroDocumento}</td>
                <td className="table-td">{e.cargo || "—"}</td>
                <td className="table-td text-xs">{e.eps || "—"}</td>
                <td className="table-td"><span className="badge bg-slate-100 text-slate-600 text-[10px]">Riesgo {e.arlNivelRiesgo}</span></td>
                <td className="table-td text-xs text-textmuted whitespace-nowrap">{e.fechaIngreso}</td>
                <td className="table-td"><span className={`badge ${e.estado === "activo" ? "bg-accent-light text-accent-dark" : "bg-slate-100 text-slate-500"}`}>{e.estado}</span></td>
                <td className="table-td text-right space-x-2 whitespace-nowrap">
                  <button className="text-navy hover:underline text-xs font-semibold disabled:opacity-40 disabled:no-underline" disabled={descargando === `${e.id}-pdf`} onClick={() => descargarCertificado(e, "pdf")}>
                    {descargando === `${e.id}-pdf` ? "Generando..." : "Certificado PDF"}
                  </button>
                  <button className="text-navy hover:underline text-xs font-semibold disabled:opacity-40 disabled:no-underline" disabled={descargando === `${e.id}-docx`} onClick={() => descargarCertificado(e, "docx")}>
                    {descargando === `${e.id}-docx` ? "Generando..." : "Word"}
                  </button>
                  {puedeEditar && e.estado === "activo" && (
                    <button className="text-red-600 hover:underline text-xs font-semibold" onClick={() => setRetirarId(e.id)}>Retirar</button>
                  )}
                </td>
              </tr>
            ))}
            {empleados.length === 0 && <tr><td colSpan={8} className="table-td text-center text-textmuted py-8">Sin empleados registrados.</td></tr>}
          </tbody>
        </table>
      </div>

      {modalAbierto && <ModalEmpleado onClose={() => setModalAbierto(false)} onCreado={() => { setModalAbierto(false); recargar(); }} />}

      {retirarId !== null && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="card w-full max-w-sm p-6 space-y-4">
            <h3 className="text-base font-semibold text-slate-800">Retirar empleado</h3>
            <p className="text-sm text-textmuted">Se marcará como retirado y se terminará su contrato activo.</p>
            <FormRetiro onCancelar={() => setRetirarId(null)} onConfirmar={retirar} />
          </div>
        </div>
      )}
    </div>
  );
}

function FormRetiro({ onCancelar, onConfirmar }: { onCancelar: () => void; onConfirmar: (fecha: string) => void }) {
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  return (
    <div className="space-y-3">
      <div><label className="block text-xs font-semibold text-slate-600 mb-1">Fecha de retiro</label>
        <input className="input-field" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} /></div>
      <div className="flex justify-end gap-2">
        <button className="btn-ghost" onClick={onCancelar}>Cancelar</button>
        <button className="btn-primary bg-red-600 hover:bg-red-700" onClick={() => onConfirmar(fecha)}>Confirmar retiro</button>
      </div>
    </div>
  );
}

function ModalEmpleado({ onClose, onCreado }: { onClose: () => void; onCreado: () => void }) {
  const [form, setForm] = useState({
    nombre: "", tipoDocumento: "CC", numeroDocumento: "", fechaIngreso: new Date().toISOString().slice(0, 10),
    cargo: "", eps: "", fondoPension: "", arlNivelRiesgo: "I", cajaCompensacion: "", banco: "", numeroCuenta: "",
    tipoCuenta: "ahorros", salarioBase: "", tipoContrato: "termino_indefinido",
  });
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const set = (campo: string, valor: string) => setForm({ ...form, [campo]: valor });

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      const empleado = await api.post("/nomina/empleados", {
        nombre: form.nombre, tipoDocumento: form.tipoDocumento, numeroDocumento: form.numeroDocumento,
        fechaIngreso: form.fechaIngreso, cargo: form.cargo || null, eps: form.eps || null, fondoPension: form.fondoPension || null,
        arlNivelRiesgo: form.arlNivelRiesgo, cajaCompensacion: form.cajaCompensacion || null, banco: form.banco || null,
        numeroCuenta: form.numeroCuenta || null, tipoCuenta: form.tipoCuenta,
      });
      if (form.salarioBase) {
        await api.post("/nomina/contratos", {
          empleadoId: empleado.id, tipoContrato: form.tipoContrato, tipoSalario: "fijo",
          salarioBase: Number(form.salarioBase), fechaInicio: form.fechaIngreso,
        });
      }
      onCreado();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo crear el empleado.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-base font-semibold text-slate-800">Nuevo empleado</h3>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2"><label className="block text-xs font-semibold text-slate-600 mb-1">Nombre completo</label>
            <input className="input-field" value={form.nombre} onChange={(e) => set("nombre", e.target.value)} required /></div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Tipo doc.</label>
            <select className="input-field" value={form.tipoDocumento} onChange={(e) => set("tipoDocumento", e.target.value)}>
              <option value="CC">CC</option><option value="CE">CE</option><option value="PA">PA</option><option value="TI">TI</option><option value="PEP">PEP</option>
            </select></div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Número documento</label>
            <input className="input-field" value={form.numeroDocumento} onChange={(e) => set("numeroDocumento", e.target.value)} required /></div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Cargo</label>
            <input className="input-field" value={form.cargo} onChange={(e) => set("cargo", e.target.value)} /></div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Fecha de ingreso</label>
            <input className="input-field" type="date" value={form.fechaIngreso} onChange={(e) => set("fechaIngreso", e.target.value)} required /></div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">EPS</label>
            <input className="input-field" value={form.eps} onChange={(e) => set("eps", e.target.value)} /></div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Fondo de pensión</label>
            <input className="input-field" value={form.fondoPension} onChange={(e) => set("fondoPension", e.target.value)} /></div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Nivel de riesgo ARL</label>
            <select className="input-field" value={form.arlNivelRiesgo} onChange={(e) => set("arlNivelRiesgo", e.target.value)}>
              <option value="I">I — Mínimo</option><option value="II">II — Bajo</option><option value="III">III — Medio</option>
              <option value="IV">IV — Alto</option><option value="V">V — Máximo</option>
            </select></div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Caja de compensación</label>
            <input className="input-field" value={form.cajaCompensacion} onChange={(e) => set("cajaCompensacion", e.target.value)} /></div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Banco</label>
            <input className="input-field" value={form.banco} onChange={(e) => set("banco", e.target.value)} /></div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Número de cuenta</label>
            <input className="input-field" value={form.numeroCuenta} onChange={(e) => set("numeroCuenta", e.target.value)} /></div>
        </div>
        <div className="border-t border-borderc pt-3">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Contrato inicial (opcional)</p>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Tipo de contrato</label>
              <select className="input-field" value={form.tipoContrato} onChange={(e) => set("tipoContrato", e.target.value)}>
                {Object.entries(TIPO_CONTRATO_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Salario base mensual</label>
              <input className="input-field" type="number" step="0.01" value={form.salarioBase} onChange={(e) => set("salarioBase", e.target.value)} /></div>
          </div>
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Guardando..." : "Crear"}</button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------- contratos
function TabContratos({ empleados, puedeCrear }: { empleados: Empleado[]; puedeCrear: boolean }) {
  const [empleadoId, setEmpleadoId] = useState("");
  const [contratos, setContratos] = useState<Contrato[]>([]);
  const [modalAbierto, setModalAbierto] = useState(false);

  const cargar = async (eid: string) => {
    if (!eid) { setContratos([]); return; }
    try { setContratos(await api.get(`/nomina/contratos?empleadoId=${eid}`)); } catch { setContratos([]); }
  };
  useEffect(() => { cargar(empleadoId); }, [empleadoId]);

  const nombreEmpleado = (id: number) => empleados.find((e) => e.id === id)?.nombre || `#${id}`;

  return (
    <div>
      <div className="flex justify-between items-end mb-3">
        <div className="max-w-xs w-full">
          <label className="block text-xs font-semibold text-slate-600 mb-1">Filtrar por empleado</label>
          <select className="input-field" value={empleadoId} onChange={(e) => setEmpleadoId(e.target.value)}>
            <option value="">Todos</option>
            {empleados.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
          </select>
        </div>
        {puedeCrear && <button className="btn-primary" onClick={() => setModalAbierto(true)}>+ Nuevo contrato</button>}
      </div>
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-borderc">
            <th className="table-th">Empleado</th><th className="table-th">Tipo</th><th className="table-th">Salario base</th>
            <th className="table-th">Inicio</th><th className="table-th">Fin</th><th className="table-th">Estado</th>
          </tr></thead>
          <tbody>
            {(empleadoId ? contratos : []).map((c) => (
              <tr key={c.id} className="border-b border-borderc last:border-0">
                <td className="table-td">{nombreEmpleado(c.empleadoId)}</td>
                <td className="table-td">{TIPO_CONTRATO_LABEL[c.tipoContrato] || c.tipoContrato}</td>
                <td className="table-td">{money(c.salarioBase)}</td>
                <td className="table-td text-xs text-textmuted whitespace-nowrap">{c.fechaInicio}</td>
                <td className="table-td text-xs text-textmuted whitespace-nowrap">{c.fechaFin || "—"}</td>
                <td className="table-td"><span className={`badge ${c.estado === "activo" ? "bg-accent-light text-accent-dark" : "bg-slate-100 text-slate-500"}`}>{c.estado}</span></td>
              </tr>
            ))}
            {!empleadoId && <tr><td colSpan={6} className="table-td text-center text-textmuted py-8">Seleccione un empleado para ver sus contratos.</td></tr>}
            {empleadoId && contratos.length === 0 && <tr><td colSpan={6} className="table-td text-center text-textmuted py-8">Sin contratos para este empleado.</td></tr>}
          </tbody>
        </table>
      </div>
      {modalAbierto && (
        <ModalContrato empleados={empleados} onClose={() => setModalAbierto(false)}
          onCreado={() => { setModalAbierto(false); cargar(empleadoId); }} />
      )}
    </div>
  );
}

function ModalContrato({ empleados, onClose, onCreado }: { empleados: Empleado[]; onClose: () => void; onCreado: () => void }) {
  const [empleadoId, setEmpleadoId] = useState("");
  const [tipoContrato, setTipoContrato] = useState("termino_indefinido");
  const [tipoSalario, setTipoSalario] = useState("fijo");
  const [salarioBase, setSalarioBase] = useState("");
  const [fechaInicio, setFechaInicio] = useState(new Date().toISOString().slice(0, 10));
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.post("/nomina/contratos", { empleadoId: Number(empleadoId), tipoContrato, tipoSalario, salarioBase: Number(salarioBase), fechaInicio });
      onCreado();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo crear el contrato.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Nuevo contrato laboral</h3>
        <p className="text-xs text-textmuted -mt-2">Al crear un nuevo contrato para un empleado, se termina automáticamente cualquier contrato activo previo.</p>
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Empleado</label>
          <select className="input-field" value={empleadoId} onChange={(e) => setEmpleadoId(e.target.value)} required>
            <option value="">Seleccione...</option>
            {empleados.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
          </select></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Tipo de contrato</label>
            <select className="input-field" value={tipoContrato} onChange={(e) => setTipoContrato(e.target.value)}>
              {Object.entries(TIPO_CONTRATO_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select></div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Tipo de salario</label>
            <select className="input-field" value={tipoSalario} onChange={(e) => setTipoSalario(e.target.value)}>
              <option value="fijo">Fijo</option><option value="integral">Integral</option>
            </select></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Salario base mensual</label>
            <input className="input-field" type="number" step="0.01" value={salarioBase} onChange={(e) => setSalarioBase(e.target.value)} required /></div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Fecha de inicio</label>
            <input className="input-field" type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} required /></div>
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Guardando..." : "Crear"}</button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------- períodos
function TabPeriodos({ periodos, puedeCrear, puedeLiquidar, recargar }: {
  periodos: Periodo[]; puedeCrear: boolean; puedeLiquidar: boolean; recargar: () => void;
}) {
  const [modalAbierto, setModalAbierto] = useState(false);

  const cerrar = async (id: number) => {
    try { await api.post(`/nomina/periodos/${id}/cerrar`, {}); recargar(); } catch { /* noop */ }
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        {puedeCrear && <button className="btn-primary" onClick={() => setModalAbierto(true)}>+ Nuevo período</button>}
      </div>
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-borderc"><th className="table-th">Período</th><th className="table-th">Desde</th><th className="table-th">Hasta</th><th className="table-th">Estado</th><th className="table-th"></th></tr></thead>
          <tbody>
            {periodos.map((p) => (
              <tr key={p.id} className="border-b border-borderc last:border-0">
                <td className="table-td font-medium">{MESES[p.mes]} {p.anio}{p.quincena ? ` · Q${p.quincena}` : ""}</td>
                <td className="table-td text-xs text-textmuted whitespace-nowrap">{p.fechaInicio}</td>
                <td className="table-td text-xs text-textmuted whitespace-nowrap">{p.fechaFin}</td>
                <td className="table-td">
                  <span className={`badge ${p.estado === "abierto" ? "bg-amber-50 text-amber-700" : p.estado === "liquidado" ? "bg-accent-light text-accent-dark" : "bg-slate-100 text-slate-500"}`}>{p.estado}</span>
                </td>
                <td className="table-td text-right">
                  {puedeLiquidar && p.estado === "liquidado" && <button className="text-red-600 hover:underline text-xs font-semibold" onClick={() => cerrar(p.id)}>Cerrar período</button>}
                </td>
              </tr>
            ))}
            {periodos.length === 0 && <tr><td colSpan={5} className="table-td text-center text-textmuted py-8">Sin períodos de nómina.</td></tr>}
          </tbody>
        </table>
      </div>
      {modalAbierto && <ModalPeriodo onClose={() => setModalAbierto(false)} onCreado={() => { setModalAbierto(false); recargar(); }} />}
    </div>
  );
}

function ModalPeriodo({ onClose, onCreado }: { onClose: () => void; onCreado: () => void }) {
  const hoy = new Date();
  const primerDia = new Date(hoy.getFullYear(), hoy.getMonth(), 1).toISOString().slice(0, 10);
  const ultimoDia = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0).toISOString().slice(0, 10);
  const [anio, setAnio] = useState(hoy.getFullYear());
  const [mes, setMes] = useState(hoy.getMonth() + 1);
  const [fechaInicio, setFechaInicio] = useState(primerDia);
  const [fechaFin, setFechaFin] = useState(ultimoDia);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.post("/nomina/periodos", { anio, mes, fechaInicio, fechaFin });
      onCreado();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo crear el período.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Nuevo período de nómina</h3>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Año</label>
            <input className="input-field" type="number" value={anio} onChange={(e) => setAnio(Number(e.target.value))} required /></div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Mes</label>
            <select className="input-field" value={mes} onChange={(e) => setMes(Number(e.target.value))}>
              {MESES.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Fecha inicio</label>
            <input className="input-field" type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} required /></div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Fecha fin</label>
            <input className="input-field" type="date" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} required /></div>
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Guardando..." : "Crear"}</button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------- novedades
function TabNovedades({ empleados, periodos, puedeCrear, puedeEditar }: {
  empleados: Empleado[]; periodos: Periodo[]; puedeCrear: boolean; puedeEditar: boolean;
}) {
  const [periodoId, setPeriodoId] = useState("");
  const [novedades, setNovedades] = useState<Novedad[]>([]);
  const [modalAbierto, setModalAbierto] = useState(false);

  const cargar = async (pid: string) => {
    if (!pid) { setNovedades([]); return; }
    try { setNovedades(await api.get(`/nomina/novedades?periodoId=${pid}`)); } catch { setNovedades([]); }
  };
  useEffect(() => { cargar(periodoId); }, [periodoId]);

  const eliminar = async (id: number) => {
    try { await api.del(`/nomina/novedades/${id}`); cargar(periodoId); } catch { /* noop */ }
  };

  const nombreEmpleado = (id: number) => empleados.find((e) => e.id === id)?.nombre || `#${id}`;

  return (
    <div>
      <div className="flex justify-between items-end mb-3">
        <div className="max-w-xs w-full">
          <label className="block text-xs font-semibold text-slate-600 mb-1">Período</label>
          <select className="input-field" value={periodoId} onChange={(e) => setPeriodoId(e.target.value)}>
            <option value="">Seleccione un período...</option>
            {periodos.map((p) => <option key={p.id} value={p.id}>{MESES[p.mes]} {p.anio}{p.quincena ? ` · Q${p.quincena}` : ""}</option>)}
          </select>
        </div>
        {puedeCrear && periodoId && <button className="btn-primary" onClick={() => setModalAbierto(true)}>+ Nueva novedad</button>}
      </div>
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-borderc"><th className="table-th">Empleado</th><th className="table-th">Tipo</th><th className="table-th">Cantidad</th><th className="table-th">Valor</th><th className="table-th">Observaciones</th><th className="table-th"></th></tr></thead>
          <tbody>
            {novedades.map((n) => (
              <tr key={n.id} className="border-b border-borderc last:border-0">
                <td className="table-td">{nombreEmpleado(n.empleadoId)}</td>
                <td className="table-td"><span className="badge bg-slate-100 text-slate-600 text-[10px]">{TIPO_NOVEDAD_LABEL[n.tipo] || n.tipo}</span></td>
                <td className="table-td">{n.cantidad ?? "—"}</td>
                <td className="table-td">{money(n.valor)}</td>
                <td className="table-td text-xs text-textmuted">{n.observaciones || "—"}</td>
                <td className="table-td text-right">
                  {puedeEditar && <button className="text-red-600 hover:underline text-xs" onClick={() => eliminar(n.id)}>Eliminar</button>}
                </td>
              </tr>
            ))}
            {!periodoId && <tr><td colSpan={6} className="table-td text-center text-textmuted py-8">Seleccione un período para ver sus novedades.</td></tr>}
            {periodoId && novedades.length === 0 && <tr><td colSpan={6} className="table-td text-center text-textmuted py-8">Sin novedades en este período.</td></tr>}
          </tbody>
        </table>
      </div>
      {modalAbierto && (
        <ModalNovedad empleados={empleados} periodoId={periodoId} onClose={() => setModalAbierto(false)}
          onCreado={() => { setModalAbierto(false); cargar(periodoId); }} />
      )}
    </div>
  );
}

function ModalNovedad({ empleados, periodoId, onClose, onCreado }: {
  empleados: Empleado[]; periodoId: string; onClose: () => void; onCreado: () => void;
}) {
  const [empleadoId, setEmpleadoId] = useState("");
  const [tipo, setTipo] = useState("bonificacion");
  const [cantidad, setCantidad] = useState("");
  const [valor, setValor] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const esHoraExtra = TIPOS_HORA_EXTRA.has(tipo);
  const [valorCalculado, setValorCalculado] = useState<number | null>(null);
  const [calculando, setCalculando] = useState(false);

  // Vista previa del valor legal de la hora extra (informativa — el backend
  // vuelve a calcularlo al guardar, con la misma fórmula, así que este número
  // nunca puede desincronizarse del que realmente queda registrado).
  useEffect(() => {
    if (!esHoraExtra || !empleadoId || !cantidad || Number(cantidad) <= 0) { setValorCalculado(null); return; }
    let cancelado = false;
    setCalculando(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/nomina/novedades/calcular-hora-extra?empleadoId=${empleadoId}&tipo=${tipo}&cantidad=${cantidad}`);
        if (!cancelado) setValorCalculado(r.valor);
      } catch {
        if (!cancelado) setValorCalculado(null);
      } finally {
        if (!cancelado) setCalculando(false);
      }
    }, 350);
    return () => { cancelado = true; clearTimeout(t); };
  }, [esHoraExtra, empleadoId, tipo, cantidad]);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.post("/nomina/novedades", {
        empleadoId: Number(empleadoId), periodoId: Number(periodoId), tipo,
        cantidad: cantidad ? Number(cantidad) : null,
        // Para horas extra el valor lo calcula el backend con la tarifa legal
        // (ver nomina_utils.calcular_valor_hora_extra) — lo que se envíe aquí
        // se ignora; para el resto de novedades sí es el valor que digitó el usuario.
        valor: esHoraExtra ? 0 : Number(valor) || 0,
        observaciones: observaciones || null,
      });
      onCreado();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo registrar la novedad.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Nueva novedad de nómina</h3>
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Empleado</label>
          <select className="input-field" value={empleadoId} onChange={(e) => setEmpleadoId(e.target.value)} required>
            <option value="">Seleccione...</option>
            {empleados.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
          </select></div>
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Tipo</label>
          <select className="input-field" value={tipo} onChange={(e) => setTipo(e.target.value)}>
            {Object.entries(TIPO_NOVEDAD_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select></div>

        {esHoraExtra ? (
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Cantidad de horas extra</label>
            <input className="input-field" type="number" step="0.01" min="0" value={cantidad} onChange={(e) => setCantidad(e.target.value)} required />
            <div className="mt-2 rounded-md bg-slate-50 border border-borderc px-3 py-2 text-sm">
              {!empleadoId ? (
                <span className="text-textmuted">Seleccione un empleado para calcular el valor.</span>
              ) : calculando ? (
                <span className="text-textmuted">Calculando...</span>
              ) : valorCalculado !== null ? (
                <span className="text-slate-700">Valor a pagar (calculado según la ley): <span className="font-semibold">{money(valorCalculado)}</span></span>
              ) : (
                <span className="text-textmuted">Indique las horas para ver el valor calculado.</span>
              )}
            </div>
            <p className="text-[11px] text-textmuted mt-1">
              El valor de la hora extra no se digita: se calcula con el salario del contrato activo del empleado y el
              recargo legal del tipo seleccionado (editables en Nómina → Parámetros).
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Cantidad (horas/días, opcional)</label>
              <input className="input-field" type="number" step="0.01" value={cantidad} onChange={(e) => setCantidad(e.target.value)} /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Valor ($)</label>
              <input className="input-field" type="number" step="0.01" value={valor} onChange={(e) => setValor(e.target.value)} required /></div>
          </div>
        )}

        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Observaciones</label>
          <input className="input-field" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} /></div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Guardando..." : "Registrar"}</button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------- liquidaciones
function TabLiquidaciones({ empleados, periodos, puedeLiquidar, puedePagar }: {
  empleados: Empleado[]; periodos: Periodo[]; puedeLiquidar: boolean; puedePagar: boolean;
}) {
  const [periodoId, setPeriodoId] = useState("");
  const [liquidaciones, setLiquidaciones] = useState<Liquidacion[]>([]);
  const [detalleId, setDetalleId] = useState<number | null>(null);
  const [detalle, setDetalle] = useState<any | null>(null);
  const [liquidando, setLiquidando] = useState(false);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [formatoDesprendible, setFormatoDesprendible] = useState("pdf");
  const [descargandoDesprendible, setDescargandoDesprendible] = useState(false);
  const [errorDesprendible, setErrorDesprendible] = useState<string | null>(null);

  const cargar = async (pid: string) => {
    if (!pid) { setLiquidaciones([]); return; }
    try { setLiquidaciones(await api.get(`/nomina/liquidaciones?periodoId=${pid}`)); } catch { setLiquidaciones([]); }
  };
  useEffect(() => { cargar(periodoId); }, [periodoId]);

  const liquidarPeriodo = async () => {
    setLiquidando(true);
    setMensaje(null);
    try {
      const res = await api.post("/nomina/liquidaciones", { periodoId: Number(periodoId) });
      setMensaje(`${res.liquidadas.length} empleado(s) liquidado(s).` + (res.errores.length ? ` ${res.errores.length} con error (revise que tengan contrato activo).` : ""));
      cargar(periodoId);
    } catch (e) {
      setMensaje(e instanceof ApiError ? e.message : "No se pudo liquidar el período.");
    } finally {
      setLiquidando(false);
    }
  };

  const abrirDetalle = async (id: number) => {
    setDetalleId(id);
    setErrorDesprendible(null);
    try { setDetalle(await api.get(`/nomina/liquidaciones/${id}`)); } catch { setDetalle(null); }
  };

  const pagar = async (id: number) => {
    try { await api.post(`/nomina/liquidaciones/${id}/pagar`, {}); setDetalleId(null); cargar(periodoId); } catch { /* noop */ }
  };

  // Desprendible de pago: mismo devengado/deducciones/neto ya liquidado,
  // en un formato pensado para el empleado — descargable en PDF, Excel o Word.
  const descargarDesprendible = async () => {
    if (!detalle) return;
    setDescargandoDesprendible(true);
    setErrorDesprendible(null);
    try {
      const nombreArchivo = `desprendible_nomina_${(detalle.empleadoNombre || "empleado").replace(/\s+/g, "_")}.${formatoDesprendible}`;
      await descargarArchivo(`/nomina/liquidaciones/${detalle.id}/desprendible?formato=${formatoDesprendible}`, nombreArchivo);
    } catch (err) {
      setErrorDesprendible(err instanceof ApiError ? err.message : "No se pudo generar el desprendible de pago.");
    } finally {
      setDescargandoDesprendible(false);
    }
  };

  const nombreEmpleado = (id: number) => empleados.find((e) => e.id === id)?.nombre || `#${id}`;

  return (
    <div>
      <div className="flex justify-between items-end mb-3">
        <div className="max-w-xs w-full">
          <label className="block text-xs font-semibold text-slate-600 mb-1">Período</label>
          <select className="input-field" value={periodoId} onChange={(e) => setPeriodoId(e.target.value)}>
            <option value="">Seleccione un período...</option>
            {periodos.map((p) => <option key={p.id} value={p.id}>{MESES[p.mes]} {p.anio}{p.quincena ? ` · Q${p.quincena}` : ""} ({p.estado})</option>)}
          </select>
        </div>
        {puedeLiquidar && periodoId && (
          <button className="btn-primary" onClick={liquidarPeriodo} disabled={liquidando}>
            {liquidando ? "Liquidando..." : "Liquidar todos los empleados activos"}
          </button>
        )}
      </div>
      {mensaje && <div className="rounded-md bg-navy/5 border border-navy/10 px-3 py-2 text-sm text-navy mb-3">{mensaje}</div>}
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-borderc">
            <th className="table-th">Empleado</th><th className="table-th">Días</th><th className="table-th">Devengado</th>
            <th className="table-th">Deducciones</th><th className="table-th">Neto pagado</th><th className="table-th">Estado</th><th className="table-th"></th>
          </tr></thead>
          <tbody>
            {liquidaciones.map((l) => (
              <tr key={l.id} className="border-b border-borderc last:border-0">
                <td className="table-td font-medium">{l.empleadoNombre || nombreEmpleado(l.empleadoId)}</td>
                <td className="table-td">{l.diasTrabajados}</td>
                <td className="table-td">{money(l.totalDevengado)}</td>
                <td className="table-td">{money(l.totalDeducciones)}</td>
                <td className="table-td font-semibold">{money(l.netoPagado)}</td>
                <td className="table-td"><span className={`badge ${l.estado === "pagada" ? "bg-accent-light text-accent-dark" : l.estado === "anulada" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>{l.estado}</span></td>
                <td className="table-td text-right"><button className="text-accent-dark hover:underline text-xs font-semibold" onClick={() => abrirDetalle(l.id)}>Ver</button></td>
              </tr>
            ))}
            {!periodoId && <tr><td colSpan={7} className="table-td text-center text-textmuted py-8">Seleccione un período para ver sus liquidaciones.</td></tr>}
            {periodoId && liquidaciones.length === 0 && <tr><td colSpan={7} className="table-td text-center text-textmuted py-8">Sin liquidaciones en este período.</td></tr>}
          </tbody>
        </table>
      </div>

      {detalleId !== null && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="card w-full max-w-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start">
              <h3 className="text-base font-semibold text-slate-800">Liquidación — {detalle?.empleadoNombre}</h3>
              <button className="text-textmuted hover:text-slate-700 text-sm" onClick={() => setDetalleId(null)}>✕</button>
            </div>
            {detalle ? (
              <>
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-borderc"><th className="table-th">Concepto</th><th className="table-th">Valor</th></tr></thead>
                  <tbody>
                    {detalle.detalle?.map((d: any, i: number) => (
                      <tr key={i} className="border-b border-borderc last:border-0">
                        <td className={`table-td ${d.tipo === "deduccion" ? "text-red-600" : ""}`}>{CONCEPTO_LABEL[d.concepto] || d.concepto}</td>
                        <td className={`table-td ${d.tipo === "deduccion" ? "text-red-600" : ""}`}>{d.tipo === "deduccion" ? "-" : ""}{money(d.valor)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="rounded-md bg-accent-light text-accent-dark px-3 py-2 text-sm font-semibold flex justify-between">
                  <span>Neto pagado</span><span>{money(detalle.netoPagado)}</span>
                </div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Aportes patronales (a cargo de la empresa)</p>
                <table className="w-full text-sm">
                  <tbody>
                    {detalle.aportesSeguridadSocial?.filter((a: any) => a.valorEmpleador > 0).map((a: any, i: number) => (
                      <tr key={i} className="border-b border-borderc last:border-0">
                        <td className="table-td capitalize">{a.concepto.replace("_", " ")}</td><td className="table-td">{money(a.valorEmpleador)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Prestaciones sociales provisionadas</p>
                <table className="w-full text-sm">
                  <tbody>
                    {detalle.prestacionesSociales?.map((p: any, i: number) => (
                      <tr key={i} className="border-b border-borderc last:border-0">
                        <td className="table-td capitalize">{p.tipo.replace("_", " ")}</td><td className="table-td">{money(p.valor)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {errorDesprendible && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{errorDesprendible}</div>}
                <div className="flex justify-between items-center gap-2 pt-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <select
                      className="rounded-lg border border-borderc bg-white px-2 py-2 text-xs text-slate-700 focus:outline-none focus:ring-4 focus:ring-accent/15 focus:border-accent"
                      value={formatoDesprendible} onChange={(e) => setFormatoDesprendible(e.target.value)}
                    >
                      <option value="pdf">PDF</option>
                      <option value="xlsx">Excel</option>
                      <option value="docx">Word</option>
                    </select>
                    <button className="btn-ghost" disabled={descargandoDesprendible} onClick={descargarDesprendible}>
                      {descargandoDesprendible ? "Generando..." : "Descargar desprendible"}
                    </button>
                  </div>
                  <div className="flex gap-2">
                    {puedePagar && detalle.estado === "liquidada" && <button className="btn-primary" onClick={() => pagar(detalle.id)}>Registrar pago</button>}
                    <button className="btn-ghost" onClick={() => setDetalleId(null)}>Cerrar</button>
                  </div>
                </div>
              </>
            ) : <p className="text-sm text-textmuted">Cargando...</p>}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- seguridad social
function TabSeguridadSocial({ periodos }: { periodos: Periodo[] }) {
  const [periodoId, setPeriodoId] = useState("");
  const [datos, setDatos] = useState<any | null>(null);

  useEffect(() => {
    api.get(periodoId ? `/nomina/seguridad-social?periodoId=${periodoId}` : "/nomina/seguridad-social").then(setDatos);
  }, [periodoId]);

  return (
    <div>
      <div className="mb-3 max-w-xs">
        <label className="block text-xs font-semibold text-slate-600 mb-1">Período</label>
        <select className="input-field" value={periodoId} onChange={(e) => setPeriodoId(e.target.value)}>
          <option value="">Todos los períodos</option>
          {periodos.map((p) => <option key={p.id} value={p.id}>{MESES[p.mes]} {p.anio}</option>)}
        </select>
      </div>
      {datos && (
        <>
          <div className="card p-5 mb-4">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Totales por concepto (PILA — Planilla Integrada de Liquidación de Aportes)</h3>
            <table className="w-full">
              <thead><tr className="border-b border-borderc"><th className="table-th">Concepto</th><th className="table-th">Aporte empleado</th><th className="table-th">Aporte empleador</th><th className="table-th">Total</th></tr></thead>
              <tbody>
                {datos.totalesPorConcepto.map((t: any) => (
                  <tr key={t.concepto} className="border-b border-borderc last:border-0">
                    <td className="table-td capitalize">{t.concepto.replace("_", " ")}</td>
                    <td className="table-td">{money(t.totalEmpleado)}</td>
                    <td className="table-td">{money(t.totalEmpleador)}</td>
                    <td className="table-td font-semibold">{money(t.totalEmpleado + t.totalEmpleador)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead><tr className="border-b border-borderc"><th className="table-th">Empleado</th><th className="table-th">Concepto</th><th className="table-th">Base</th><th className="table-th">Empleado</th><th className="table-th">Empleador</th></tr></thead>
              <tbody>
                {datos.detalle.map((d: any) => (
                  <tr key={d.id} className="border-b border-borderc last:border-0">
                    <td className="table-td">{d.empleadoNombre}</td>
                    <td className="table-td capitalize">{d.concepto.replace("_", " ")}</td>
                    <td className="table-td">{money(d.base)}</td>
                    <td className="table-td">{money(d.valorEmpleado)}</td>
                    <td className="table-td">{money(d.valorEmpleador)}</td>
                  </tr>
                ))}
                {datos.detalle.length === 0 && <tr><td colSpan={5} className="table-td text-center text-textmuted py-8">Sin aportes registrados.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- prestaciones sociales
function TabPrestaciones({ empleados }: { empleados: Empleado[] }) {
  const [empleadoId, setEmpleadoId] = useState("");
  const [prestaciones, setPrestaciones] = useState<any[]>([]);

  useEffect(() => {
    api.get(empleadoId ? `/nomina/prestaciones-sociales?empleadoId=${empleadoId}` : "/nomina/prestaciones-sociales").then(setPrestaciones);
  }, [empleadoId]);

  return (
    <div>
      <div className="mb-3 max-w-xs">
        <label className="block text-xs font-semibold text-slate-600 mb-1">Empleado</label>
        <select className="input-field" value={empleadoId} onChange={(e) => setEmpleadoId(e.target.value)}>
          <option value="">Todos</option>
          {empleados.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
        </select>
      </div>
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-borderc"><th className="table-th">Empleado</th><th className="table-th">Tipo</th><th className="table-th">Base</th><th className="table-th">Valor provisionado</th><th className="table-th">Estado</th></tr></thead>
          <tbody>
            {prestaciones.map((p) => (
              <tr key={p.id} className="border-b border-borderc last:border-0">
                <td className="table-td">{p.empleadoNombre}</td>
                <td className="table-td capitalize">{p.tipo.replace("_", " ")}</td>
                <td className="table-td">{money(p.base)}</td>
                <td className="table-td font-semibold">{money(p.valor)}</td>
                <td className="table-td"><span className="badge bg-accent-light text-accent-dark">{p.estado}</span></td>
              </tr>
            ))}
            {prestaciones.length === 0 && <tr><td colSpan={5} className="table-td text-center text-textmuted py-8">Sin prestaciones sociales provisionadas aún.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- nómina electrónica
function TabNominaElectronica() {
  const [documentos, setDocumentos] = useState<any[]>([]);
  const [xml, setXml] = useState<{ numero: string; xml: string } | null>(null);

  const cargar = async () => { try { setDocumentos(await api.get("/nomina/nomina-electronica")); } catch { /* noop */ } };
  useEffect(() => { cargar(); }, []);

  const verXml = async (id: number) => {
    try { setXml(await api.get(`/nomina/nomina-electronica/${id}/xml`)); } catch { /* noop */ }
  };

  return (
    <div>
      <div className="rounded-md bg-navy/5 border border-navy/10 px-3 py-2 text-xs text-navy mb-3">
        Documento Soporte de Pago de Nómina Electrónica — estructura de referencia inspirada en la Resolución DIAN 000013 de 2021.
        Se genera en estado <strong>simulado</strong> (no timbrado ante la DIAN); la integración queda lista para conectar el
        webservice de un proveedor tecnológico autorizado.
      </div>
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-borderc"><th className="table-th">Número</th><th className="table-th">Empleado</th><th className="table-th">CUNE</th><th className="table-th">Estado DIAN</th><th className="table-th">Fecha</th><th className="table-th"></th></tr></thead>
          <tbody>
            {documentos.map((d) => (
              <tr key={d.id} className="border-b border-borderc last:border-0">
                <td className="table-td font-mono text-xs">{d.numero}</td>
                <td className="table-td">{d.empleadoNombre}</td>
                <td className="table-td font-mono text-[10px] text-textmuted">{d.cune?.slice(0, 24)}...</td>
                <td className="table-td"><span className="badge bg-amber-50 text-amber-700">{d.estadoDian}</span></td>
                <td className="table-td text-xs text-textmuted whitespace-nowrap">{d.fechaGeneracion}</td>
                <td className="table-td text-right"><button className="text-accent-dark hover:underline text-xs font-semibold" onClick={() => verXml(d.id)}>Ver XML</button></td>
              </tr>
            ))}
            {documentos.length === 0 && <tr><td colSpan={6} className="table-td text-center text-textmuted py-8">Sin documentos de nómina electrónica generados. Genérelos desde el detalle de una liquidación.</td></tr>}
          </tbody>
        </table>
      </div>
      {xml && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="card w-full max-w-2xl p-6 space-y-3 max-h-[85vh] overflow-y-auto">
            <h3 className="text-base font-semibold text-slate-800">{xml.numero}</h3>
            <pre className="bg-slate-50 rounded-md p-3 text-[11px] overflow-x-auto whitespace-pre-wrap">{xml.xml}</pre>
            <div className="flex justify-end"><button className="btn-primary" onClick={() => setXml(null)}>Cerrar</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- parámetros
function TabParametros({ puedeEditar }: { puedeEditar: boolean }) {
  const [parametros, setParametros] = useState<any[]>([]);
  const [edicion, setEdicion] = useState<Record<string, string>>({});

  const cargar = async () => { try { setParametros(await api.get("/nomina/parametros")); } catch { /* noop */ } };
  useEffect(() => { cargar(); }, []);

  const guardar = async (codigo: string, metadatos: any) => {
    const clave = Object.keys(metadatos)[0];
    const nuevoValor = edicion[codigo];
    if (nuevoValor === undefined) return;
    try {
      await api.put(`/nomina/parametros/${codigo}`, { metadatos: { ...metadatos, [clave]: Number(nuevoValor) } });
      cargar();
    } catch { /* noop */ }
  };

  return (
    <div className="card overflow-hidden">
      <table className="w-full">
        <thead><tr className="border-b border-borderc"><th className="table-th">Parámetro</th><th className="table-th">Tarifa (%)</th>{puedeEditar && <th className="table-th"></th>}</tr></thead>
        <tbody>
          {parametros.map((p) => {
            const clave = Object.keys(p.metadatos)[0];
            return (
              <tr key={p.codigo} className="border-b border-borderc last:border-0">
                <td className="table-td">{p.nombre}</td>
                <td className="table-td">
                  {puedeEditar ? (
                    <input className="input-field w-28" type="number" step="0.001"
                      defaultValue={p.metadatos[clave]}
                      onChange={(e) => setEdicion({ ...edicion, [p.codigo]: e.target.value })} />
                  ) : `${p.metadatos[clave]}%`}
                </td>
                {puedeEditar && (
                  <td className="table-td">
                    <button className="text-accent-dark hover:underline text-xs font-semibold" onClick={() => guardar(p.codigo, p.metadatos)}>Guardar</button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
